import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaRegistrationRenewalPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Registration Renewal</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Vehicle Registration Renewal</h1>
        <p className="text-obsidian-300">How to renew your Nevada vehicle registration — deadlines, methods, and what you need.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">When to Renew</h2>
          <p>Nevada vehicle registrations expire annually. The DMV typically mails a renewal notice approximately <strong>45–60 days before expiration</strong>. Nevada does <strong>not</strong> offer a grace period — your registration must be renewed by the expiration date to avoid penalties.</p>
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm font-semibold flex items-center space-x-2"><i className="ri-error-warning-line"></i><span>No Grace Period</span></p>
            <p className="text-red-700 text-xs mt-1">Nevada law does not provide a grace period for expired registrations. Late fees begin accruing immediately after the expiration date.</p>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Renewal Methods</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: 'ri-global-line', title: 'Online', desc: 'Renew at dmv.nv.gov if your vehicle passed emissions and you have no outstanding issues. Most convenient option.' },
              { icon: 'ri-mail-line', title: 'By Mail', desc: 'Send your renewal notice with payment to the address on the notice. Allow extra time for processing.' },
              { icon: 'ri-building-line', title: 'In Person', desc: 'Visit a Nevada DMV office or use an authorized third-party provider like Speedy DMV Services.' },
            ].map(m => (
              <div key={m.title} className="border border-obsidian-100 rounded-xl p-5 text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-gold-50 rounded-full mx-auto mb-3">
                  <i className={`${m.icon} text-gold-600 text-lg`}></i>
                </div>
                <p className="font-semibold text-obsidian-900 text-sm mb-1">{m.title}</p>
                <p className="text-obsidian-500 text-xs">{m.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">What You Need to Renew</h2>
          <ul className="space-y-3">
            {[
              'Current Nevada vehicle insurance (must be Nevada-licensed)',
              'Passed emissions test (if required for your county and vehicle)',
              'Renewal notice (or vehicle plate number and last 4 of VIN)',
              'Payment for registration fees and governmental services tax',
              'Any outstanding fees or penalties must be paid',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-line text-green-500"></i>
                </div>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Late Renewal Penalties</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Penalty Type</th><th className="text-right py-2 font-semibold text-obsidian-900">Amount</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[['Registration Late Penalty', '$6.00 per month'], ['Tax Penalty', '10% of past-due GST (min $6, calculated every 15 days)'], ['Pro-rated Taxes', 'For the period vehicle was unregistered']].map(([l, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-right font-medium text-obsidian-900">{a}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need Help Renewing?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services can handle your renewal quickly. No lines, no hassle.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaRegistrationRenewalPage;
